import { definePreset } from "@primevue/themes";
import Aura from "@primevue/themes/aura";

/**
 * PrimeVue theme, driven entirely by src/assets/tokens.css. No hex values here.
 *
 * `darkModeSelector: "none"` stays despite the app having two themes, and that is the point:
 * PrimeVue evaluates this one rule set, and tokens.css switches the theme underneath it.
 *
 * The ramp is mapped by ROLE, not luminance — 0 is the page background and 950 body text,
 * which is what Aura's light rules assume. Each index keeps its job on either palette, so one
 * mapping serves both. Touch it only if tokens.css gains or loses a surface level.
 */
export const Preset = definePreset(Aura, {
  options: {
    darkModeSelector: "none",
  },
  semantic: {
    primary: {
      50: "var(--brand-50)",
      100: "var(--brand-100)",
      200: "var(--brand-200)",
      300: "var(--brand-300)",
      400: "var(--brand-400)",
      500: "var(--brand-500)",
      600: "var(--brand-600)",
      700: "var(--brand-700)",
      800: "var(--brand-800)",
      900: "var(--brand-900)",
      950: "var(--brand-950)",
    },
    formField: {
      borderRadius: "0.5rem",
      lg: {
        fontSize: "1rem",
        padding: {
          y: "1rem",
        },
      },
    },
    colorScheme: {
      light: {
        surface: {
          0: "var(--surface-base)",
          50: "var(--surface-raised)",
          100: "var(--surface-muted)",
          200: "var(--border-line)",
          300: "var(--border-strong)",
          400: "var(--text-subtle)",
          500: "var(--text-subtle)",
          600: "var(--text-secondary)",
          700: "var(--text-secondary)",
          800: "var(--text-primary)",
          900: "var(--text-primary)",
          950: "var(--text-primary)",
        },
        primary: {
          color: "var(--brand-500)",
          contrastColor: "var(--text-on-brand)",
          hoverColor: "var(--brand-400)",
          activeColor: "var(--brand-600)",
        },
        highlight: {
          background: "var(--brand-subtle)",
          focusBackground: "var(--brand-subtle)",
          color: "var(--brand-500)",
          focusColor: "var(--brand-500)",
        },
        content: {
          background: "var(--surface-raised)",
          hoverBackground: "var(--surface-muted)",
          borderColor: "var(--border-line)",
        },
        overlay: {
          select: {
            background: "var(--surface-raised)",
            borderColor: "var(--border-line)",
            color: "var(--text-primary)",
          },
          popover: {
            background: "var(--surface-raised)",
            borderColor: "var(--border-line)",
            color: "var(--text-primary)",
          },
          modal: {
            // The design floats every modal on the chrome colour, a step *below*
            // the cards behind it — so panels dropped inside a dialog
            // (--surface-raised) read as raised rather than dissolving into it.
            background: "var(--surface-panel)",
            borderColor: "var(--border-line)",
            color: "var(--text-primary)",
          },
        },
        text: {
          color: "var(--text-primary)",
          hoverColor: "var(--text-primary)",
          mutedColor: "var(--text-secondary)",
          hoverMutedColor: "var(--text-primary)",
        },
        formField: {
          // Fields are drawn as wells, not as raised chips: the design sinks every
          // input to the chrome colour and lets the outline do the separating —
          // on a card, in a modal, or straight on the page canvas.
          background: "var(--surface-panel)",
          disabledBackground: "var(--surface-muted)",
          filledBackground: "var(--surface-muted)",
          filledHoverBackground: "var(--surface-muted)",
          filledFocusBackground: "var(--surface-muted)",
          borderColor: "var(--border-input)",
          hoverBorderColor: "var(--text-secondary)",
          focusBorderColor: "var(--brand-500)",
          invalidBorderColor: "var(--state-danger)",
          color: "var(--text-primary)",
          disabledColor: "var(--text-subtle)",
          placeholderColor: "var(--text-subtle)",
          invalidPlaceholderColor: "var(--state-danger)",
          floatLabelColor: "var(--text-subtle)",
          floatLabelFocusColor: "var(--brand-500)",
          floatLabelActiveColor: "var(--text-subtle)",
          floatLabelInvalidColor: "var(--state-danger)",
          iconColor: "var(--text-secondary)",
        },
      },
    },
  },

  components: {
    // An empty box is grey, not transparent: on a dark row a hollow outline
    // disappears, and the design draws every unchecked control as a filled tile.
    checkbox: {
      root: {
        background: "var(--surface-control)",
        borderColor: "var(--border-control)",
        hoverBorderColor: "var(--border-control)",
        checkedBackground: "var(--brand-500)",
        checkedBorderColor: "var(--brand-500)",
        checkedHoverBackground: "var(--brand-600)",
        checkedHoverBorderColor: "var(--brand-600)",
        focusBorderColor: "var(--brand-500)",
      },
      icon: {
        checkedColor: "var(--text-on-brand)",
        checkedHoverColor: "var(--text-on-brand)",
      },
    },
    // Deliberately *not* the checkbox treatment above: the design draws a radio
    // as a ring, never as a filled disc. Off is a grey outline over whatever it
    // sits on, on is the same outline in brand with a brand dot inside — so the
    // control keeps its shape and only gains colour, rather than turning into a
    // solid blue coin with a white hole (N7AIT-1182).
    radiobutton: {
      root: {
        background: "transparent",
        borderColor: "var(--border-control)",
        hoverBorderColor: "var(--brand-500)",
        checkedBackground: "transparent",
        checkedBorderColor: "var(--brand-500)",
        checkedHoverBackground: "transparent",
        checkedHoverBorderColor: "var(--brand-600)",
        focusBorderColor: "var(--brand-500)",
        checkedFocusBorderColor: "var(--brand-500)",
      },
      icon: {
        checkedColor: "var(--brand-500)",
        checkedHoverColor: "var(--brand-600)",
      },
    },
    datatable: {
      headerCell: {
        color: "var(--text-subtle)",
        // A sorted column is marked by its label and chevron turning brand, the
        // way the design draws it — Aura would also paint the whole cell with the
        // highlight fill, which reads as a selected cell rather than a sort.
        selectedBackground: "var(--surface-raised)",
        selectedColor: "var(--brand-500)",
      },
      columnTitle: {
        fontWeight: "600",
      },
      row: {
        hoverBackground: "var(--surface-muted)",
        // Aura's default striped background resolves to surface.50 — the same
        // token as the row itself — which makes `stripedRows` invisible here.
        stripedBackground: "var(--surface-muted)",
      },
    },
    // Keys here must match Aura's own spelling exactly — a nested
    // `border: { radius }` where Aura declares a flat `borderRadius` emits
    // nothing at all and silently leaves the default in place.
    dialog: {
      root: {
        borderRadius: "1rem",
      },
      title: {
        // 16px — the design gives every modal heading the card-title step.
        fontSize: "1rem",
      },
    },
    // The widget is only ever a wrapper here — every view draws its own dashed
    // drop zone in the `empty` slot, so the chrome PrimeVue adds around it (a
    // panel fill, a ring and a padded content box) would double up on the
    // design. Stripped back to nothing so the slot is what shows.
    fileupload: {
      root: {
        background: "transparent",
        borderColor: "transparent",
      },
      header: {
        padding: "0",
      },
      content: {
        padding: "0",
      },
    },
    toast: {
      colorScheme: {
        light: {
          info: {
            background: "var(--toast-info)",
            borderColor: "var(--state-info)",
            color: "var(--state-info)",
            detailColor: "var(--text-secondary)",
            closeButton: {
              hoverBackground: "var(--state-info-subtle)",
              focusRing: { color: "var(--state-info)", shadow: "0 0 0 0.2rem var(--state-info-subtle)" },
            },
          },
          success: {
            background: "var(--toast-success)",
            borderColor: "var(--state-success)",
            color: "var(--state-success)",
            detailColor: "var(--text-secondary)",
            closeButton: {
              hoverBackground: "var(--state-success-subtle)",
              focusRing: { color: "var(--state-success)", shadow: "0 0 0 0.2rem var(--state-success-subtle)" },
            },
          },
          warn: {
            background: "var(--toast-warning)",
            borderColor: "var(--state-warning)",
            color: "var(--state-warning)",
            detailColor: "var(--text-secondary)",
            closeButton: {
              hoverBackground: "var(--state-warning-subtle)",
              focusRing: { color: "var(--state-warning)", shadow: "0 0 0 0.2rem var(--state-warning-subtle)" },
            },
          },
          error: {
            background: "var(--toast-danger)",
            borderColor: "var(--state-danger)",
            color: "var(--state-danger)",
            detailColor: "var(--text-secondary)",
            closeButton: {
              hoverBackground: "var(--state-danger-subtle)",
              focusRing: { color: "var(--state-danger)", shadow: "0 0 0 0.2rem var(--state-danger-subtle)" },
            },
          },
          secondary: {
            background: "var(--toast-surface)",
            borderColor: "var(--border-line)",
            color: "var(--text-primary)",
            detailColor: "var(--text-secondary)",
            closeButton: {
              hoverBackground: "var(--surface-muted)",
              focusRing: { color: "var(--brand-500)", shadow: "0 0 0 0.2rem var(--brand-subtle)" },
            },
          },
          contrast: {
            background: "var(--toast-muted)",
            borderColor: "var(--border-strong)",
            color: "var(--text-primary)",
            detailColor: "var(--text-secondary)",
            closeButton: {
              hoverBackground: "var(--surface-raised)",
              focusRing: { color: "var(--brand-500)", shadow: "0 0 0 0.2rem var(--brand-subtle)" },
            },
          },
        },
      },
    },
  },
});
